// ─────────────────────────────────────────────────────────────
//  activation.js — funnel event tracking (client side)
//
//  Sends POST /api/activation/track for the 7 primary events.
//  Pre-auth events carry an anon_id pulled from localStorage
//  ('kindra_anon_id'). Post-auth events are auto-attached to the
//  authenticated user on the backend (via access_token cookie).
//
//  Every "first-time" event uses a localStorage guard so it fires
//  once per browser per user (e.g. first_home_viewed, first_chat_started).
//  Best-effort: failures are silent, never block UX, never throw.
// ─────────────────────────────────────────────────────────────
import axios from './api';
import { API } from './api';

const ANON_KEY = 'kindra_anon_id';
const FIRED_KEY_PREFIX = 'kindra_act_fired__';

function getAnonId() {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = 'a_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

export function track(event, props) {
  try {
    const anon_id = getAnonId();
    axios.post(
      `${API}/activation/track`,
      { event, anon_id, props: props || {} },
      { timeout: 4000 },
    ).catch(() => {});
  } catch { /* noop */ }
}

// Fire an event ONCE per browser per user/anon.
// Key includes the event name so different "first" events don't clobber each other.
export function trackOnce(event, props) {
  try {
    const k = FIRED_KEY_PREFIX + event;
    if (localStorage.getItem(k)) return;
    localStorage.setItem(k, '1');
    track(event, props);
  } catch {
    track(event, props);
  }
}

// Clear the "once" flags — called on logout so the next user gets fresh firsts.
export function resetActivationFlags() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(FIRED_KEY_PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch { /* noop */ }
}

export const ACTIVATION_EVENTS = {
  HOMEPAGE_VISIT:      'homepage_visit',
  CTA_CLICK:           'cta_click',
  REGISTER_COMPLETE:   'register_complete',
  ONBOARDING_START:    'onboarding_start',
  ONBOARDING_FINISH:   'onboarding_finish',
  FIRST_HOME_VIEWED:   'first_home_viewed',
  FIRST_CHAT_STARTED:  'first_chat_started',
  // Stage 20B+ Wave F — founder-spec canonical pilot event names.
  // Where existing events overlap (e.g. REGISTER_COMPLETE ↔ signup_completed),
  // both fire so the dashboard can read either.
  LANDING_VIEW:        'landing_view',
  SIGNUP_STARTED:      'signup_started',
  SIGNUP_COMPLETED:    'signup_completed',
  ONBOARDING_COMPLETED:'onboarding_completed',
  FIRST_CHAT:          'first_chat',
  FIRST_UPLOAD:        'first_upload',
  REPORT_COMPLETED:    'report_completed',
  REPORT_SAVED:        'report_saved',
  APPOINTMENT_ADDED:   'appointment_added',
  BILLING_VIEWED:      'billing_viewed',
  CHECKOUT_STARTED:    'checkout_started',
};
